import React, {
  useState,
  useMemo,
  createContext,
  useContext,
  forwardRef,
  isValidElement,
  cloneElement,
  useRef,
  ReactElement,
  Ref,
  useEffect,
} from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  arrow,
} from '@floating-ui/react';
import { useMediaQuery } from 'usehooks-ts';
import * as S from './Tooltip.styles';
import { TooltipProps, TooltipOptions } from './Tooltip.types';

function useTooltip({
  initialOpen = false,
  placement = 'right-start',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  offset: offsetValue = 20,
  allowedTriggers = ['hover', 'focus'],
  disabled = false,
  fallbackPlacements = ['top-end'],
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const arrowRef = useRef(null);
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)', {
    initializeWithValue: false,
  });

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip({
        fallbackPlacements,
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef, padding: 20 }),
    ],
  });

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const { context } = data;

  const hover = useHover(context, {
    move: false,
    enabled: !disabled && canHover && allowedTriggers.includes('hover'),
  });
  const focus = useFocus(context, {
    enabled: !disabled && allowedTriggers.includes('focus'),
  });

  const interactions = useInteractions([hover, focus]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      arrowRef,
    }),
    [open, setOpen, interactions, data]
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

export function Tooltip({
  children,
  content,
  className,
  dataTestId,
  ...options
}: TooltipProps) {
  const tooltip = useTooltip(options);
  return (
    <TooltipContext.Provider value={tooltip}>
      <TooltipTrigger>{children}</TooltipTrigger>
      {tooltip.open && (
        <TooltipContent className={className} data-testid={dataTestId}>
          {content}
        </TooltipContent>
      )}
    </TooltipContext.Provider>
  );
}

const TooltipTrigger = forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({ children, asChild = true, ...props }, propRef) {
  const context = useTooltipContext();

  const childElement = children as ReactElement<Record<string, unknown>> & {
    ref?: Ref<unknown>;
  };
  const mergedRef = useMergeRefs([propRef, childElement.ref]);

  if (asChild && isValidElement(children)) {
    return (
      <S.TooltipWrapper ref={context.refs.setReference}>
        {cloneElement(childElement, {
          ...context.getReferenceProps({
            ref: mergedRef,
            ...props,
            ...childElement.props,
            // @ts-ignore
            'data-state': context.open ? 'open' : 'closed',
          }),
        })}
      </S.TooltipWrapper>
    );
  }

  return (
    <button
      ref={mergedRef}
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

const TooltipContent = forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(function TooltipContent({ style, className, ...props }, propRef) {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!context.open) return null;

  return (
    <FloatingPortal>
      <S.TooltipBox
        ref={ref}
        style={{
          ...context.floatingStyles,
          ...style,
        }}
        className={className}
        {...context.getFloatingProps(props)}
      >
        {props.children}
        <S.Arrow ref={context.arrowRef} context={context.context} />
      </S.TooltipBox>
    </FloatingPortal>
  );
});
